import React, {useState, useEffect, useCallback, useMemo} from 'react';
import '../styles/contact.css';
import * as emailjs from "@emailjs/browser";
import ChatBot from "./ChatBot";
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import ContactSubmitted from "./ContactSubmitted";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        interest: 'general',
        marketingConsent: false
    });

    const [formErrors, setFormErrors] = useState({});
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formTouched, setFormTouched] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [phoneCountryData, setPhoneCountryData] = useState(null);
    const [submitted, setSubmitted] = useState('We\'ve received your message. Lumō, our WhatsApp assistant and Scriptō, our email assistant will be reaching out to you within 24 hours.');

    const interestOptions = [
        {value: 'general', label: 'General Inquiry'},
        {value: 'demo', label: 'Request a Demo'},
        {value: 'pricing', label: 'Pricing Information'},
        {value: 'partnership', label: 'Partnership Opportunity'},
        {/*{ value: 'support', label: 'Technical Support' }*/}
    ];

    const handleChange = (e) => {
        console.log('Handling change...');
        const {name, value, type, checked} = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Mark field as touched
        setFormTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prevErrors => ({
                ...prevErrors,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = "Name is required";
        }

        if (!formData.email.trim()) {
            errors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = "Email address is invalid";
        }

        if (!formData.message.trim()) {
            errors.message = "Message is required";
        } else if (formData.message.trim().length < 10) {
            errors.message = "Message must be at least 10 characters";
        }

        if (formData.phone && !validatePhone(formData.phone)) {
            errors.phone = "Phone number is invalid";
        }

        return errors;
    };

    const validatePhone = (phone) => {
        if (!phone || !phoneCountryData) {
            console.log("Phone number is required");
            return false
        }

        // Check 2: Country-specific length validation
        // Get the expected number of digits from the country format string (e.g., "+.. (...) ...-....")
        const expectedDigits = phoneCountryData.format.match(/\./g).length;

        // Get the actual number of digits the user has typed
        const actualDigits = phone.match(/\d/g)?.length || 0;
        const valid = actualDigits === expectedDigits
        console.log('Validating phone '+phone+', expected '+expectedDigits+'digits and got '+actualDigits+'digits. Returning boolean '+valid);
        return valid
    }

    // Validate on blur
    const handleBlur = (e) => {
        console.log('Handle blur')
        const {name} = e.target;

        setFormTouched(prev => ({
            ...prev,
            [name]: true
        }));

        const fieldErrors = validateField(name, formData[name]);
        if (fieldErrors) {
            setFormErrors(prev => ({
                ...prev,
                [name]: fieldErrors
            }));
        }
    };

    const validateField = (fieldName, value) => {
        console.log('Validating field {} with value {}',fieldName, value);
        switch (fieldName) {
            case 'name':
                return !value.trim() ? "Name is required" : null;
            case 'email':
                if (!value.trim()) return "Email is required";
                if (!/\S+@\S+\.\S+/.test(value)) return "Email address is invalid";
                return null;
            case 'message':
                if (!value.trim()) return "Message is required";
                if (value.trim().length < 10) return "Message must be at least 10 characters";
                return null;
            case 'phone':
                if (value && !validatePhone(value))
                    return "Phone number is invalid";
                return null;
            default:
                return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            // Mark all fields as touched to show errors
            const touchedFields = {};
            Object.keys(formData).forEach(key => {
                touchedFields[key] = true;
            });
            setFormTouched(touchedFields);
            return;
        }

        setIsSubmitting(true);

        try {

            formData.year = new Date().getFullYear();

            {/** emailjs
             .send(
             "service_jgnpx7g", // Replace with your EmailJS Service ID
             "template_1y4vqil", // Replace with your EmailJS Template ID
             formData,
             "KpU407PeDxzqolG2W" // Replace with your EmailJS Public Key
             );**/
            }

            const payload = {
                "body": {
                    "company_name": formData.company,
                    "description": formData.message,
                    "industry": formData.interest,
                    "contact_details": {
                        "email": formData.email,
                        "phone": formData.phone,
                        "name": formData.name,
                    }
                }
            }

            const fetchWithTimeout = async (url, options, timeout = 5000) => {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeout);

                try {
                    const response = await fetch(url, {
                        ...options,
                        signal: controller.signal
                    });
                    clearTimeout(timeoutId); // Clear timeout as request completed
                    setSubmitSuccess(true);
                    return response; // Return the response object directly
                } catch (error) {
                    clearTimeout(timeoutId); // Always clear timeout
                    if (error.name === 'AbortError') {
                        // Re-throw specific error for timeout
                        setSubmitted('OK, this is embarrassing 🙈! There was a small hiccup when sending your message. If you have NOT received a WhatsApp message or an email from us, please re-submit your message.')
                        throw new Error(`Request timed out after ${timeout}ms`);
                    }else{
                        setSubmitted("Well, that didn't go as planned 😅. Don't worry, it's a quick fix: just try sending your message again.")
                    }
                    // Re-throw any other network errors
                    throw error;
                }
            };

            const waResponse = await fetchWithTimeout(
                "https://z184yo4b1e.execute-api.eu-west-1.amazonaws.com/prod/intellommerce/wa/bot",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                    mode: 'no-cors', // Bypass CORS
                },
                5000 // 5 second timeout for both the request and response processing
            );

            if (waResponse.status === 429) {
                setSubmitted('You have already sent us a similar request that is being processed! You will be contacted via WhatsApp or email.')
            }

            // Reset form
            setFormData({
                name: '',
                email: '',
                company: '',
                phone: '',
                message: '',
                interest: 'general',
                marketingConsent: false
            });

            setFormTouched({});
        } catch (error) {
        } finally {
            setIsSubmitting(false);
            setFormSubmitted(true);
        }
    };

    const phoneInputProps = useMemo(() => ({
        name: 'phone',
        id: 'phone',
        required: true,
        onBlur: handleBlur,
        disabled: isSubmitting,
    }), [handleBlur, isSubmitting]);

    return (
        <section id="contact" className="contact-section">
            <div className="contact-background">
                <div className="contact-shape shape-1"></div>
                <div className="contact-shape shape-2"></div>
            </div>

            <div className="contact-container">
                <div className="contact-info">
                    <h2>Get in <span className="highlight">Touch</span></h2>
                    <p className="contact-intro">
                        Ready to revolutionize your digital marketing with AI? Contact us today to learn how our
                        automated intelligent assistants can transform your business.
                    </p>

                    <div className="contact-methods">
                        <div className="contact-method">
                            <div className="method-icon email-icon">
                                <svg viewBox="0 0 24 24" width="24" height="24">
                                    <path fill="currentColor"
                                          d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                </svg>
                            </div>
                            <div className="method-details">
                                <h3>Email Us</h3>
                                <a href="mailto:support@intellommerce.ai">support@intellommerce.ai</a>
                                <a href="mailto:billing@intellommerce.ai">billing@intellommerce.ai</a>
                                <a href="mailto:privacy@intellommerce.ai">privacy@intellommerce.ai</a>
                            </div>
                        </div>

                        <div className="contact-method">
                            <div className="method-icon phone-icon">
                                <svg viewBox="0 0 24 24" width="24" height="24">
                                    <path fill="currentColor"
                                          d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                </svg>
                            </div>
                            <div className="method-details">
                                <h3>WhatsApp Us</h3>
                                <a href="https://wa.link/sx24dj">+32 (476) 37 12 07</a>
                                <p>Available 24 hours, 7 days a week!</p>
                            </div>
                        </div>

                        {/*   <div className="contact-method">
                            <div className="method-icon location-icon">
                                <svg viewBox="0 0 24 24" width="24" height="24">
                                    <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                            </div>
                            <div className="method-details">
                                <h3>Mail Us</h3>
                                <p>De Schrijverstraat 26, B01</p>
                                <p>Aalst, Oost-Vlanderen, 9300</p>
                            </div>
                        </div> */}
                    </div>

                    <div className="social-links">
                        <a href="https://www.linkedin.com/company/intellommerce/" className="social-link"
                           aria-label="LinkedIn">
                            <svg viewBox="0 0 24 24" width="24" height="24">
                                <path fill="currentColor"
                                      d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                            </svg>
                        </a>
                        <a href="#" className="social-link" aria-label="Twitter">
                            <svg viewBox="0 0 24 24" width="24" height="24">
                                <path fill="currentColor"
                                      d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                            </svg>
                        </a>
                        <a href="#" className="social-link" aria-label="Facebook">
                            <svg viewBox="0 0 24 24" width="24" height="24">
                                <path fill="currentColor"
                                      d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
                            </svg>
                        </a>
                        <a href="#" className="social-link" aria-label="Instagram">
                            <svg viewBox="0 0 24 24" width="24" height="24">
                                <path fill="currentColor"
                                      d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                            </svg>
                        </a>
                    </div>
                </div>

                <div className="contact-form-container">
                    {formSubmitted ? (
                        <ContactSubmitted
                            onClose={() => {setFormSubmitted(false)
                                setFormData({
                                name: '',
                                email: '',
                                company: '',
                                phone: '',
                                message: '',
                                interest: 'general',
                                marketingConsent: false
                            });}}
                            message={submitted}
                            success={submitSuccess}
                        />
                    ) : (
                        <form id="contact-form" className="contact-form" onSubmit={handleSubmit}>
                            <h3>Send Us a Message</h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={formTouched.name && formErrors.name ? 'error' : ''}
                                        disabled={isSubmitting}
                                    />
                                    {formTouched.name && formErrors.name &&
                                        <span className="error-message">{formErrors.name}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={formTouched.email && formErrors.email ? 'error' : ''}
                                        disabled={isSubmitting}
                                    />
                                    {formTouched.email && formErrors.email &&
                                        <span className="error-message">{formErrors.email}</span>}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="company">Company Name *</label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number (preferably your WhatsApp number) *</label>

                                    <PhoneInput
                                        country={'us'}
                                        value={formData.phone}
                                        onChange={(value, country, e, formattedValue) => {
                                            setFormData({ ...formData, phone: formattedValue });
                                            setPhoneCountryData(country);
                                            handleChange(e)
                                        }}
                                        inputProps={phoneInputProps}
                                        // Apply the error class to the container
                                        containerClass={`phone-input-container ${formTouched.phone && formErrors.phone ? 'error' : ''}`}
                                        buttonClass="phone-dropdown"
                                        inputClass="phone-input"
                                        countryCodeEditable={false}
                                    />

                                    {formTouched.phone && formErrors.phone && (
                                        <span className="error-message">{formErrors.phone}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="interest">What are you interested in?</label>
                                <select
                                    id="interest"
                                    name="interest"
                                    value={formData.interest}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                >
                                    {interestOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Your Message *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="5"
                                    value={formData.message}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className={formTouched.message && formErrors.message ? 'error' : ''}
                                    disabled={isSubmitting}
                                ></textarea>
                                {formTouched.message && formErrors.message &&
                                    <span className="error-message">{formErrors.message}</span>}
                            </div>

                            <div className="form-group checkbox-group">
                                <input
                                    type="checkbox"
                                    id="marketingConsent"
                                    name="marketingConsent"
                                    checked={formData.marketingConsent}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                />
                                <label htmlFor="marketingConsent">
                                    I agree to receive marketing communications from Intellommerce. You can unsubscribe
                                    at
                                    any time.
                                </label>
                            </div>

                            <button
                                type="submit"
                                className={`primary-button submit-button ${isSubmitting ? 'submitting' : ''}`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner"></span>
                                        Sending...
                                    </>
                                ) : 'Send Message'}
                            </button>

                            <p className="form-disclaimer">
                                By submitting this form, you agree to our <a href="?modal=privacy">Privacy
                                Policy</a> and <a
                                href="?modal=terms">Terms
                                of Service</a>.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    )
        ;
};

export default Contact;